import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  useTheme,
  alpha,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Agriculture as FarmerIcon,
  People as PeopleIcon,
  MonetizationOn as MoneyIcon,
  Receipt as ReceiptIcon,
  Assessment as ReportIcon,
  ShoppingCart as PurchaseIcon,
  Store as SaleIcon,
  Inventory2 as StockIcon,
  AutoGraph as CommissionIcon,
  CheckCircle as CheckIcon,
  Smartphone as MobileIcon,
  SyncAlt as SyncIcon,
  BarChart as ChartIcon,
  Verified as VerifiedIcon,
  Share as ShareIcon,
  AutoAwesome as PremiumIcon,
  CurrencyRupee,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Image imports (replace with your own)
import apmcDashboard from "./Images/ApmcDashboard.png";
import apmcTransactions from "./Images/ApmcTransactions.png";
import apmcReports from "./Images/accounting.png";
import apmcMobile from "./Images/ApmcMobile.png";
import muhvaAgent from "./Images/apmc/m_agent.webp";
import muhvaLogo from "./Images/apmc/m_l.webp";
import muhvaMenu from "./Images/apmc/m_menu.webp";
import muhvaSrv from "./Images/apmc/m_srv.webp";

const APMCCommissionAgent = () => {
  const theme = useTheme();

  const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha("#8cc419", 0.1)} 0%, ${alpha(
      "#19c444ff",
      0.05
    )} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: 20,
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    height: "75%",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, #27a22d, #8cc419)`,
    },
  }));

  const features = [
    {
      title: "Farmer & Buyer Ledger",
      description:
        "Maintain live balances, statements, and history for each farmer and merchant.",
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Purchase & Sale Entry",
      description:
        "Record mandi transactions and auto-update ledgers & stock in real time.",
      icon: <PurchaseIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Commission & Expense Calculation",
      description:
        "Automatically calculate commission, brokerage, and other expenses per transaction.",
      icon: <CommissionIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Stock & Inventory Control",
      description:
        "Track produce arrivals, dispatches, and current stock with complete traceability.",
      icon: <StockIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Reports & Analytics",
      description:
        "Generate profit, payment, and outstanding reports instantly.",
      icon: <ReportIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "GST Ready Billing",
      description:
        "Stay compliant with GST-ready invoices and digital record keeping.",
      icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const muhvaDetailedFeatures = [
    {
      title: "Secure & Scalable Enterprise Platform",
      image: muhvaLogo,
      description: "Built on a robust, cloud-native architecture, the APMC Mahuva application ensures your sensitive financial data and daily transactions are highly secure and backed up in real-time.",
      bullets: [
        "Military-grade data encryption for all transactions",
        "Real-time cloud synchronization preventing data loss",
        "99.9% uptime architecture tailored for high-volume trading",
        "Trusted by APMC Mahuva and surrounding agricultural ecosystems"
      ]
    },
    {
      title: "Streamlined UX/UI & Mobile Navigation",
      image: muhvaMenu,
      description: "Engineered for maximum operational efficiency, the intuitive mobile interface allows agents to navigate complex ledgers, inventory metrics, and reports with zero friction.",
      bullets: [
        "Zero-latency module switching tailored for fast-paced trading",
        "Centralized app menu for one-tap access to critical features",
        "Responsive design optimized for all modern mobile devices",
        "Reduced training time with a highly intuitive user flow"
      ]
    },
    {
      title: "Automated Service & Workflow Management",
      image: muhvaSrv,
      description: "A centralized hub that automates your entire workflow—from live purchase entries to complex, multi-tier commission calculations—eliminating manual errors entirely.",
      bullets: [
        "Automated multi-tier brokerage and commission tracking",
        "Live purchase and sale entry synchronization",
        "Instant reconciliation of expenses and farmer payouts",
        "Comprehensive digital service catalog for commission agents"
      ]
    },
    {
      title: "Real-Time Agent Analytics Dashboard",
      image: muhvaAgent,
      description: "Monitor your business pulse instantly. The agent dashboard provides live synchronization of farmer ledgers, outstanding balances, and dynamic stock inventory.",
      bullets: [
        "Live dashboard offering a 360-degree view of your business",
        "Real-time synchronization of farmer ledger balances",
        "Dynamic stock inventory tracking and reporting",
        "Instant push notifications for critical ledger updates"
      ]
    }
  ];

  return (
    <Box
      sx={{
        pb: 8,
        background: "linear-gradient(135deg, #f8fff9ff 0%, #ffffff 100%)",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            "#8cc419",
            0.08
          )} 0%, ${alpha("#2ba32c", 0.08)} 100%)`,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<PremiumIcon />}
                label="APMC Commission Agent System"
                color="primary"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg,   #8cc419  0%, #27a22d 100%)`,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background:
                    "linear-gradient(135deg, #27a22d 0%, #8cc419 100%)",
                  // background: `linear-gradient(135deg, "${theme.palette.primary.main}" 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Manage Your Mandi Business Digitally
              </Typography>

              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Streamline purchase, sale, commission, and farmer payments with
                one easy-to-use platform.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MoneyIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #27a22d 0%, #8cc419 100%)",
                  }}
                  onClick={() => window.open('https://wa.me/918000516051', '_blank')}
                >
                  Start Free Demo
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    color: "#36a62c",
                    borderColor: "#36a62c",
                    "&:hover": {
                      borderColor: "#36a62c",
                      backgroundColor: "rgba(54, 166, 44, 0.08)",
                    },
                  }}
                >
                  Learn More
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={apmcDashboard}
                alt="APMC Dashboard"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Powerful Tools for{" "}
            <Box component="span" color="#27a22d">
              Commission Agents
            </Box>
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mx: "auto" }}>
            Simplify mandi operations with automated ledgers, billing, and
            commission tracking.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 12, md: 4 }} key={index}>
              <PremiumCard>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      width: 70,
                      height: 70,
                      mb: 3,
                      mx: "auto",
                      border: `2px solid ${alpha(feature.color, 0.2)}`,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </PremiumCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Integration Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <PremiumCard>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ mb: 1 }}
            textAlign={"center"}
          >
            Full Control Over{" "}
            <span style={{ color: "#27a22d" }}>Mandi Transactions</span>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 3 }}
            textAlign={"center"}
          >
            Auto-sync entries with ledgers, stock, and reports in real time.
          </Typography>
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 6 }}>
              <List>
                {[
                  "Record purchase and sale transactions",
                  "Auto commission & expense calculation",
                  "Ledger & stock update instantly",
                  "Generate reports & settlements",
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "h6",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={apmcTransactions}
                alt="Transactions"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>
          </Grid>
        </PremiumCard>
      </Container>

      {/* Mobile Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={apmcMobile}
              alt="APMC Mobile"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
              <span style={{ color: "#27a22d" }}>APMC Mobile</span> Manage
              Business on the Go
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Access reports, update ledgers, and generate invoices directly
              from your mobile.
            </Typography>
            <Stack spacing={2}>
              {[
                "Farmer payment updates",
                "Invoice sharing via WhatsApp",
                "Live stock tracking",
                "Profit summary on mobile",
                "Cloud backup & sync",
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <MobileIcon sx={{ mr: 2, color: "#27a22d" }} />
                  <Typography variant="h6" fontWeight={500}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Live App Examples Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Trusted by <span style={{ color: "#27a22d" }}>Live Mandi APMCs</span>
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mx: "auto" }}>
            See how real APMC centers and communities are using our system daily.
          </Typography>
        </Box>
        <Grid container spacing={3} justifyContent="center">
          {[
            "APMC Mahuva",
            "APMC Talaja",
            "APMC Kalavad",
            "APMC Mandal",
            "APMC Savarkundla",
            "GNBS Directory",
            "Community Center",
          ].map((app, index) => (
            <Grid size={{ xs: "auto" }} key={index}>
              <Chip
                label={app}
                icon={<VerifiedIcon style={{ color: "#27a22d" }} />}
                sx={{
                  px: 2,
                  py: 3,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  backgroundColor: alpha("#8cc419", 0.1),
                  border: `1px solid ${alpha("#27a22d", 0.3)}`,
                  "&:hover": {
                    backgroundColor: alpha("#8cc419", 0.2),
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How APMC Muhva Works Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 8 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2 }}>
            APMC MUHVA Commission Agent: <span style={{ color: "#27a22d" }}>Deep Dive</span>
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mx: "auto", maxWidth: 800 }}>
            Discover how the APMC Muhva application leverages modern cloud technology to digitize and automate your daily mandi operations, providing seamless real-time synchronization across your entire business.
          </Typography>
        </Box>

        {muhvaDetailedFeatures.map((feature, index) => (
          <PremiumCard key={index} sx={{ mb: 6, p: { xs: 3, md: 5 } }}>
            <Grid
              container
              spacing={6}
              alignItems="center"
              direction={{ xs: "column-reverse", md: index % 2 === 0 ? "row" : "row-reverse" }}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                  <span style={{ color: "#27a22d" }}>{index + 1}.</span> {feature.title}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>

                <List sx={{ p: 0 }}>
                  {feature.bullets.map((bullet, idx) => (
                    <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={bullet}
                        primaryTypographyProps={{
                          variant: "body1",
                          fontWeight: 500,
                          color: "text.primary"
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    height: { xs: 300, md: 400 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#8cc419', 0.05),
                    borderRadius: 4,
                    p: 4,
                    border: `1px dashed ${alpha('#27a22d', 0.3)}`
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      maxHeight: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </PremiumCard>
        ))}
      </Container>

      {/* Final CTA */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <PremiumCard>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2 }}>
            Ready to Modernize Your Mandi Operations?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Boost productivity and transparency with our all-in-one APMC
            Commission Agent System.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg,   #8cc419  0%, #27a22d 100%)`,
              }}
              startIcon={<CurrencyRupee />}
              onClick={() => window.open('https://wa.me/918000516051', '_blank')}
            >
              Book Live Demo
            </Button>

            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600,
                color: "#36a62c",
                borderColor: "#36a62c",
                "&:hover": {
                  borderColor: "#36a62c",
                  backgroundColor: "rgba(54, 166, 44, 0.08)",
                },
              }}
              onClick={() => window.open('https://wa.me/918000516051', '_blank')}
            >
              Get Started
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            ✓ GST Ready • ✓ Auto Ledger • ✓ Commission Calculated Instantly
          </Typography>
        </PremiumCard>
      </Container>
    </Box>
  );
};

export default APMCCommissionAgent;
